import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, UrlSegment } from '@angular/router';
import { NEVER, Observable, of } from 'rxjs';
import { catchError, delay, switchMap } from 'rxjs/operators';
import { RefreshTokenService, CurrentTenantRegister, NotificationService, RegionService, SessionService, AuthService } from '@parashift/shared/services';

@Injectable({
  providedIn: 'root'
})
export class PdcGuardService {
  private router = inject(Router);
  private refreshTokenService = inject(RefreshTokenService);
  private currentTenantRegister = inject(CurrentTenantRegister);
  private sessionService = inject(SessionService);
  private regionService = inject(RegionService);
  private notificationService = inject(NotificationService);
  private authService = inject(AuthService);

  private currentTenantId: string;

  canMatch(segments: UrlSegment[]): Observable<boolean> {
    const tenantId = this.getCurrentTenantId(segments);
    if (tenantId !== this.currentTenantId) {
      this.currentTenantId = tenantId;
      return this.refreshTokenService.get(this.currentTenantId).pipe(
        delay(200),
        switchMap(() => this.currentTenantRegister.getTenant(this.currentTenantId)),
        switchMap(() => this.regionService.setRegion(this.currentTenantRegister.tenant.region)),
        switchMap(() => this.checkRouteAndTenantPermissions()),
        catchError((error) => {
          switch (error?.status) {
            case 401:
              // The httpErrorInterceptor already forwards this 401 to the id-api login (a full-page
              // redirect). Keep the guard pending instead of resolving false, so the router doesn't
              // fall through to the ** wildcard (a 404 flash) before the browser navigates away.
              return NEVER;
            case 403:
              // Two kinds of 403: "no access to this tenant" vs "no permission for this resource
              // within a tenant you belong to". If the token isn't scoped to the requested tenant
              // it's the former -> picker; otherwise keep the user and show missing-configuration.
              // (currentTenantId is a URL string and session_tenant_id a numeric JWT claim; === won't
              // coerce, so the requested id must be cast to Number.)
              if (Number(this.currentTenantId) !== this.sessionService.session_tenant_id) {
                this.authService.logoutOfTenant();
              } else {
                this.goToMissingConfiguration();
              }
              break;
            default:
              this.goToMissingConfiguration();
          }
          return of(false);
        })
      );
    } else {
      return of(true);
    }
  }

  canDeactivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    if (this.currentTenantId === this.getCurrentTenantId(route.url)) {
      this.currentTenantId = undefined;
      this.currentTenantRegister.setTenant(undefined);
    }

    return of(true);
  }

  private getCurrentTenantId(segments: UrlSegment[]) {
    return segments?.[1]?.path ?? undefined;
  }

  private checkRouteAndTenantPermissions() {
    const allowed = this.currentTenantRegister.isCurrentUserAllowed();

    if (!allowed) {
      this.notificationService.error({
        title: $localize `:@@pdc_guard.forbidden_title:Forbidden`,
        description: $localize `:@@pdc_guard.forbidden_description:You dont have permissions to access these resources`
      });
      this.goToMissingConfiguration();
      return of(false);
    }

    return of(true);
  }

  private goToMissingConfiguration() {
    const language = this.sessionService.user?.language || 'en';
    this.router.navigate(['/' + language, 'missing-configuration', { reason: 'pdc' }]);
  }
}
